import { EventsManager } from '@game-libs/events-manager';
import { type GUIGameState } from '../../types/game-types';
import { GameplayEvents } from '../../types/game-events';
import { inspirationReducer } from './generator-reducers/inspiration.reducer';
import { roundTo } from '@game-libs/exc-libs';
import { unitStats } from '../../stats';
import { useGameStatusStore } from '../zustand/useGameStatusStore';
import { useCurrencyStore } from '../zustand/useCurrencyStore';
import { useDragonHealthStore } from '../zustand/useDragonHealthStore';
import { useGameStatsStore } from '../zustand/useGameStatsStore';
import { useUpgradesStore } from '../zustand/useUpgradesStore';

export const clickDragonReducer = (state: GUIGameState) => {
  const { emit } = EventsManager();

  // Click damage / crit / inspiration-on-click all depend on persisted upgrade state (Zustand).
  const upgrades = useUpgradesStore.getState().upgrades;

  let damage = unitStats.click.damagePerClick ?? 0;

  const criticalChance = upgrades.click.perfectClick.multiplier;

  const criticalMultiplier = upgrades.click.criticalStrike.multiplier;

  const isCriticalHit = Math.random() < criticalChance / 100;

  if (isCriticalHit) {
    damage *= criticalMultiplier;

    emit(GameplayEvents.CRITICAL_HIT, {
      damage,
    });
  }

  const generatedInspiration = upgrades.click.naturalLeader.multiplier;

  const manaRegen = upgrades.wizard.magicMouse.multiplier;

  const newStunRemaining = Math.max(
    0,
    useGameStatusStore.getState().stunEffectTickDurationRemaining -
    upgrades.click.wakeupCall.multiplier * 1000,
  );
  useGameStatusStore.getState().setStunEffectTickDurationRemaining(newStunRemaining);

  const currency = useCurrencyStore.getState();
  const nextMana = roundTo(
    Math.min(upgrades.wizard.manaPool.multiplier, manaRegen + currency.mana),
  );
  const nextInspiration = inspirationReducer(currency.inspiration, generatedInspiration, upgrades);
  useCurrencyStore.getState().sync({ mana: nextMana, inspiration: nextInspiration });

  // Dragon HP lives only in Zustand (GUI state no longer carries it).
  useDragonHealthStore.getState().applyDamage(damage);

  useGameStatsStore.getState().incrementActiveLevelClicks(1);

  // Gold / HP / inspiration / stun live in Zustand — avoid a new GUIGameState reference so React context does not re-render the whole tree.
  return state;
};
